import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export interface LanguageOption {
  code: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private languageSubject = new BehaviorSubject<string>('en');
  public language$ = this.languageSubject.asObservable();
  // Injected, not `new TranslateService()`: a manually constructed instance never gets the
  // app's configured TranslateLoader (see TranslateModule.forRoot() in app.config.ts), so
  // .use() has nothing to fetch translations with and its observable never emits. That left
  // AppComponent's *ngIf="translationsLoaded" gate waiting on an event that could never
  // fire - an indefinitely blank screen, not just a slow one.
  private translate = inject(TranslateService);

  constructor() {
    this.translate.setDefaultLang('en');
  }

  // Mock method to simulate fetching available languages from an API
  getMockAvailableLanguages(): Observable<LanguageOption[]> {
    return of([
      { code: 'en', name: 'English' },
      { code: 'es', name: 'Español' }, //spanish
      { code: 'fr', name: 'Français' }, //french
      { code: 'de', name: 'Deutsch' }, //german
    ]);
  }

  /**
   * Determines the boot-time language from storage and loads its translations, resolving
   * once they're ready. Called from APP_INITIALIZER (app.config.ts) alongside session
   * hydration via Promise.all, so the two run concurrently instead of the translation
   * fetch only starting after hydration finishes (which is what AppComponent.ngOnInit
   * previously did, since it can't run until APP_INITIALIZER resolves) - that used to make
   * every cold load take (session time + translation time) instead of
   * max(session time, translation time).
   */
  initializeLanguage(): Promise<void> {
    const userStr = localStorage.getItem('currentUser');
    let lang = 'en';
    try {
      lang = userStr ? (JSON.parse(userStr).language ?? localStorage.getItem('language') ?? 'en') : (localStorage.getItem('language') ?? 'en');
    } catch {
      lang = localStorage.getItem('language') ?? 'en';
    }
    return this.setLanguage(lang);
  }

  // Method to change the current language. Returns a promise that resolves once that
  // language's translations are loaded (resolves immediately from ngx-translate's cache
  // if already loaded), so callers that need to know when it's safe to render can await it.
  setLanguage(languageCode: string): Promise<void> {
    localStorage.setItem('language', languageCode);
    this.languageSubject.next(languageCode);
    return firstValueFrom(this.translate.use(languageCode)).then(() => undefined);
  }
}
